import React, { useEffect, useState } from 'react';
import { BarChart3, PhoneCall, PlusCircle } from 'lucide-react';
import { ServiceExecutiveService } from '../../services/serviceExecutiveService';
import ServiceRequestsList from './ServiceRequestsList';
import ServiceRequestDetailModal from './ServiceRequestDetailModal';
import ServiceExecutiveComplaintRegistrationModal from './ServiceExecutiveComplaintRegistrationModal';

const ServiceExecutiveDashboard = () => {
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [listKey, setListKey] = useState(0);

  const loadDashboardStats = async () => {
    if (!user?.id) return;
    setLoadingStats(true);
    const result = await ServiceExecutiveService.getDashboardStats(user.id);
    setDashboardStats(result.data);
    setLoadingStats(false);
  };

  useEffect(() => {
    loadDashboardStats();
  }, [user?.id]);

  const handleDetailOpen = (request) => {
    setSelectedRequest(request);
    setDetailOpen(true);
  };

  const handleDetailClose = () => {
    setDetailOpen(false);
    setSelectedRequest(null);
    loadDashboardStats();
    setListKey((prev) => prev + 1);
  };

  return (
    <div className="space-y-6 pb-20 lg:pb-0">
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
        <h1 className="text-3xl font-bold mb-2">Service Executive Dashboard</h1>
        <p className="text-white/80">Customer service and request execution overview</p>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Work Overview</h2>
          {loadingStats && <span className="text-sm text-gray-500">Refreshing...</span>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-sm text-gray-500">Pending</p>
            <p className="text-2xl font-bold text-gray-900">{dashboardStats?.pending_requests || 0}</p>
          </div>
          <div className="rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-sm text-gray-500">In Progress</p>
            <p className="text-2xl font-bold text-gray-900">{dashboardStats?.in_progress_requests || 0}</p>
          </div>
          <div className="rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-sm text-gray-500">Completed</p>
            <p className="text-2xl font-bold text-gray-900">{dashboardStats?.completed_requests || 0}</p>
          </div>
          <div className="rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-sm text-gray-500">Today</p>
            <p className="text-2xl font-bold text-gray-900">{dashboardStats?.today_requests || 0}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Quick Actions</h2>
          <button className="text-sm text-indigo-600 hover:text-indigo-700">View All</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            type="button"
            onClick={() => setRegistrationOpen(true)}
            className="flex items-center gap-2 px-4 py-3 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
          >
            <PlusCircle size={18} />
            Register Complaint
          </button>
          <button
            type="button"
            onClick={() => alert('Call Customer action')}
            className="flex items-center gap-2 px-4 py-3 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
          >
            <PhoneCall size={18} />
            Call Customer
          </button>
          <button
            type="button"
            onClick={() => alert('Analytics coming soon')}
            className="flex items-center gap-2 px-4 py-3 rounded-lg bg-purple-600 text-white hover:bg-purple-700"
          >
            <BarChart3 size={18} />
            Analytics
          </button>
        </div>
      </div>

      <ServiceRequestsList
        key={listKey}
        user={user}
        status={statusFilter}
        onStatusChange={setStatusFilter}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        onOpenDetail={handleDetailOpen}
        onOpenRegistration={() => setRegistrationOpen(true)}
      />

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-sm lg:hidden">
        <div className="flex justify-around py-2 text-xs text-gray-700">
          <button type="button" onClick={() => setRegistrationOpen(true)} className="flex flex-col items-center">
            <PlusCircle size={18} className="text-indigo-600" />
            Register
          </button>
          <button type="button" onClick={() => setStatusFilter('pending')} className="flex flex-col items-center">
            <span className="font-semibold text-emerald-600">●</span>
            Pending
          </button>
          <button type="button" onClick={() => setStatusFilter('in_progress')} className="flex flex-col items-center">
            <span className="font-semibold text-yellow-600">●</span>
            In Progress
          </button>
          <button type="button" onClick={() => setStatusFilter('completed')} className="flex flex-col items-center">
            <span className="font-semibold text-green-600">●</span>
            Completed
          </button>
        </div>
      </div>

      <ServiceRequestDetailModal
        open={detailOpen}
        requestId={selectedRequest?.id}
        onClose={handleDetailClose}
        user={user}
      />

      <ServiceExecutiveComplaintRegistrationModal
        open={registrationOpen}
        onClose={() => setRegistrationOpen(false)}
        executiveId={user?.id}
        executiveName={user?.name || ''}
        onSuccess={() => {
          loadDashboardStats();
          setListKey((prev) => prev + 1);
        }}
      />
    </div>
  );
};

export default ServiceExecutiveDashboard;

